import { priceForExport } from "./pricing.js";

export type LanguageCode = "en" | "ar" | "de" | "hi" | "ta" | "te" | "fr" | "es" | "ja" | "ko" | "th" | "tr";

export interface Language {
  code: LanguageCode;
  name: string;
  nativeName: string;
  direction: "ltr" | "rtl";
  /** What a line is measured in: whitespace-separated words, or non-space characters. */
  unit: "words" | "characters";
  /** Units spoken per second at a natural pace. */
  pace: number;
  /** How long the language runs compared with the same text in English. */
  expansion: number;
  voiceModel: "eleven_multilingual_v2" | "eleven_v3";
  /**
   * Whether the whole video — cards, captions, callouts and, later, product
   * screens — is translated, or only the voice and subtitles. Set from the
   * image-translation test of 2026-10-14.
   */
  wholeVideo: boolean;
}

const v2 = "eleven_multilingual_v2";
const v3 = "eleven_v3";

/**
 * `pace` and `expansion` are estimates, to be tuned from listening to real
 * output rather than treated as facts about the languages.
 */
export const LANGUAGES: readonly Language[] = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    direction: "ltr",
    unit: "words",
    pace: 2.5,
    expansion: 1.0,
    voiceModel: v2,
    wholeVideo: true,
  },
  {
    code: "ar",
    name: "Arabic",
    nativeName: "العربية",
    direction: "rtl",
    unit: "words",
    pace: 2.1,
    expansion: 1.0,
    voiceModel: v2,
    wholeVideo: false,
  },
  {
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    direction: "ltr",
    unit: "words",
    pace: 2.1,
    expansion: 1.3,
    voiceModel: v2,
    wholeVideo: true,
  },
  {
    code: "hi",
    name: "Hindi",
    nativeName: "हिन्दी",
    direction: "ltr",
    unit: "words",
    pace: 2.4,
    expansion: 1.1,
    voiceModel: v2,
    wholeVideo: false,
  },
  {
    code: "ta",
    name: "Tamil",
    nativeName: "தமிழ்",
    direction: "ltr",
    unit: "words",
    pace: 1.8,
    expansion: 1.3,
    voiceModel: v2,
    wholeVideo: false,
  },
  {
    code: "te",
    name: "Telugu",
    nativeName: "తెలుగు",
    direction: "ltr",
    unit: "words",
    pace: 1.8,
    expansion: 1.2,
    voiceModel: v3,
    wholeVideo: false,
  },
  {
    code: "fr",
    name: "French",
    nativeName: "Français",
    direction: "ltr",
    unit: "words",
    pace: 2.4,
    expansion: 1.2,
    voiceModel: v2,
    wholeVideo: true,
  },
  {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    direction: "ltr",
    unit: "words",
    pace: 2.6,
    expansion: 1.2,
    voiceModel: v2,
    wholeVideo: true,
  },
  {
    code: "ja",
    name: "Japanese",
    nativeName: "日本語",
    direction: "ltr",
    unit: "characters",
    pace: 7.0,
    expansion: 0.6,
    voiceModel: v2,
    wholeVideo: true,
  },
  {
    code: "ko",
    name: "Korean",
    nativeName: "한국어",
    direction: "ltr",
    unit: "characters",
    pace: 5.5,
    expansion: 0.7,
    voiceModel: v2,
    wholeVideo: true,
  },
  {
    code: "th",
    name: "Thai",
    nativeName: "ไทย",
    direction: "ltr",
    unit: "characters",
    pace: 9.0,
    expansion: 1.0,
    voiceModel: v3,
    wholeVideo: true,
  },
  {
    code: "tr",
    name: "Turkish",
    nativeName: "Türkçe",
    direction: "ltr",
    unit: "words",
    pace: 2.0,
    expansion: 1.1,
    voiceModel: v2,
    wholeVideo: true,
  },
];

export type LanguageDepth = "voice" | "video";

/** What a language is made at: the whole video only where the language supports it. */
export function depthFor(language: Language, asked: LanguageDepth): LanguageDepth {
  return asked === "video" && language.wholeVideo ? "video" : "voice";
}

export function languageByCode(code: string): Language | undefined {
  return LANGUAGES.find((l) => l.code === code);
}

/** Every language but the source. */
export function targetLanguages(source: string): Language[] {
  return LANGUAGES.filter((l) => l.code !== source);
}

/** The most a line may hold to be spoken in `seconds`, with a tenth to spare. */
export function lineBudget(seconds: number, language: Language): number {
  return Math.max(3, Math.floor(seconds * language.pace * 0.9));
}

/** Words, or characters without whitespace (counted by code point, not UTF-16 unit). */
export function measureLine(text: string, language: Language): number {
  if (language.unit === "words") return text.split(/\s+/).filter(Boolean).length;
  return [...text].filter((ch) => !/\s/.test(ch)).length;
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map((v) => (v === undefined ? "null" : canonical(v))).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const parts = Object.keys(obj)
      .sort()
      .filter((k) => obj[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`);
    return `{${parts.join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

/**
 * A stable 16-hex-character hash of any JSON-like value, independent of key
 * order. Synchronous and free of node:crypto so it runs in the browser too.
 * FNV-1a, 64 bit, done as two 32-bit halves. Not a security hash.
 */
export function fingerprint(value: unknown): string {
  const text = canonical(value);
  // 64-bit prime 0x100000001b3 = high 0x100, low 0x1b3.
  let hi = 0xcbf29ce4;
  let lo = 0x84222325;
  const bytes = new TextEncoder().encode(text);
  for (const byte of bytes) {
    lo = (lo ^ byte) >>> 0;
    const loLow = lo * 0x1b3;
    const newLo = loLow >>> 0;
    const carry = Math.floor(loLow / 0x100000000);
    hi = (Math.imul(hi, 0x1b3) + lo * 0x100 + carry) >>> 0;
    lo = newLo;
  }
  return hi.toString(16).padStart(8, "0") + lo.toString(16).padStart(8, "0");
}

interface LanguageRates {
  anthropicInputPer1mTokens: number;
  anthropicOutputPer1mTokens: number;
  elevenLabsPer1kCharacters: number;
}

interface LanguageCostInput {
  englishCharacters: number;
  language: Language;
  /** Characters of on-screen text (cards, callouts) to translate as well; none by default. */
  screenCharacters?: number;
}

/** Estimated provider dollars to translate and voice one video into one language. */
export function languageCostEstimate(input: LanguageCostInput, rates: LanguageRates): number {
  const { englishCharacters, language } = input;
  const screen = input.screenCharacters ?? 0;
  const tokensIn = (englishCharacters + screen) / 4 + 1500;
  const tokensOut = (((englishCharacters + screen) * language.expansion) / 4) * 1.3;
  const voiceCharacters = englishCharacters * language.expansion;
  return (
    (tokensIn * rates.anthropicInputPer1mTokens) / 1e6 +
    (tokensOut * rates.anthropicOutputPer1mTokens) / 1e6 +
    (voiceCharacters * rates.elevenLabsPer1kCharacters) / 1000
  );
}

export function languagePrice(input: LanguageCostInput, rates: LanguageRates): number {
  return priceForExport(languageCostEstimate(input, rates));
}
